import { DateTime } from 'luxon';

import { isSplitTxn } from 'app-components/flux/transactions/transactionsPredicates';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

import type { OrderCandidate } from './merchantOrderCandidate';
import { type MerchantOrderSkipReason, merchantOrderSkipReason } from './merchantOrdersTypes';

/**
 * Two cents. An order total is reassembled from items, shipping,
 * charges, adjustments and tax, so it accumulates more rounding than a receipt total does.
 */
export const DEFAULT_AMOUNT_TOLERANCE_CENTS = 2;

/**
 * Forward-only, unlike the receipt matcher's ±5 days. A receipt is dated the day you paid; Amazon
 * charges when it ships, which is always on or after the order date and can be over a week later.
 * Wide enough to absorb the gap between the card being charged and the charge clearing.
 */
export const DEFAULT_DATE_WINDOW_DAYS = 10;

/**
 * Descriptor stems a merchant's own name does not cover. Amazon reaches the register as
 * `AMZN Mktp US*RT4G92OP3` and `AMZN Digital` as often as `Amazon.com`, and a substring search for
 * "amazon" alone misses every `AMZN` form.
 *
 * Keyed by the merchant's name lowercased rather than by a camelCase identifier, because the key
 * has to line up with `merchantName` as the server sends it.
 */
export const KNOWN_MERCHANT_PAYEE_PATTERNS: Record<string, string[]> = {
  amazon: ['amzn'],
};

/**
 * Every pattern we know, across every merchant — used only when an order does not name its own.
 * Derived rather than written out so adding a merchant above cannot leave this list behind. Keys
 * are included alongside their values because the key *is* the merchant's primary pattern.
 */
export const DEFAULT_PAYEE_PATTERNS = Object.entries(KNOWN_MERCHANT_PAYEE_PATTERNS).flatMap(
  ([merchantName, patterns]) => [merchantName, ...patterns],
);

/**
 * The merchant's own name, plus any extra stems we know it by. Searching the name itself means a
 * new merchant matches on day one without a dictionary entry — the entry only buys the abbreviated
 * forms.
 */
export const payeePatternsForOrder = (merchantName: string | null | undefined): string[] => {
  const name = merchantName?.trim().toLowerCase();
  if (!name) {
    return DEFAULT_PAYEE_PATTERNS;
  }
  return [name, ...(KNOWN_MERCHANT_PAYEE_PATTERNS[name] ?? [])];
};

/** `cpData` is read for its payee fields only; dates come from `postedOn`, as elsewhere. */
export type MatchableTransaction = Pick<
  TransactionProps,
  'id' | 'amount' | 'postedOn' | 'payee' | 'accountId' | 'cpData' | 'split' | 'memo'
>;

export type MatchableAccount = {
  id: string;
  /**
   * Digits only, from `accountsUtils.getAccountFinalFourDigits`. Not the raw `accountNumberMasked`:
   * that field exists on both the account and its aggregator, and only the aggregator's is reliable.
   */
  finalFourDigits?: string | null;
};

export type MatchOrderCandidatesOptions = {
  amountToleranceCents?: number;
  dateWindowDays?: number;
  payeePatterns?: string[];
  /** ISO date. Future-dated rows are never candidates, mirroring the register's own behaviour. */
  today?: string;
  /** Supplying accounts enables last-four scoping; omitting it searches every account. */
  accounts?: MatchableAccount[];
};

export type MerchantOrderMatch = {
  candidate: OrderCandidate;
  transactionId: string;
};

export type MerchantOrderMatchSkip = {
  candidate: OrderCandidate;
  reason: MerchantOrderSkipReason;
};

export type MatchOrderCandidatesResult = {
  matches: MerchantOrderMatch[];
  skipped: MerchantOrderMatchSkip[];
};

const toCents = (value: number): number => Math.round(value * 100);

/** Which field carries the retailer varies, so any of the three counts as a hit. */
const matchesPayee = (transaction: MatchableTransaction, patterns: string[]): boolean =>
  [transaction.payee, transaction.cpData?.payee, transaction.cpData?.inferredPayee].some((field) =>
    patterns.some((pattern) => field?.toLowerCase().includes(pattern)),
  );

/** Empty when there is no hint or no accounts to resolve it against, which broadens the search. */
const accountIdsForLastFour = (accounts: MatchableAccount[] | undefined, lastFour: string | undefined): Set<string> =>
  new Set(
    !accounts || !lastFour
      ? []
      : accounts.filter((account) => (account.finalFourDigits ?? '').endsWith(lastFour)).map((account) => account.id),
  );

/**
 * Transactions that could plausibly belong to *any* order. Run once over the register rather than
 * per order, since the payee and split checks do not depend on which order we are looking at.
 */
const eligibleTransactions = (
  transactions: MatchableTransaction[],
  patterns: string[],
  today: string | undefined,
): MatchableTransaction[] =>
  transactions.filter((transaction) => {
    if (!transaction.id || isSplitTxn(transaction)) {
      // A transaction the user already split is the closest stand-in we have for "already matched";
      // there is no server field recording the link yet (OQ-10).
      return false;
    }
    // Dates compare as plain strings, as they do throughout the register: `postedOn` is
    // `yyyy-MM-dd`, which sorts chronologically.
    if (today && transaction.postedOn > today) {
      return false;
    }
    return matchesPayee(transaction, patterns);
  });

const transactionsForCandidate = (
  candidate: OrderCandidate,
  transactions: MatchableTransaction[],
  {
    amountToleranceCents,
    dateWindowDays,
    accounts,
    patterns,
  }: Required<Pick<MatchOrderCandidatesOptions, 'amountToleranceCents' | 'dateWindowDays'>> &
    Pick<MatchOrderCandidatesOptions, 'accounts'> & { patterns: string[] },
): string[] => {
  const targetCents = Math.abs(toCents(candidate.transaction.amount));
  const windowStart = candidate.transaction.postedOn;
  const windowEnd = DateTime.fromISO(windowStart).plus({ days: dateWindowDays }).toISODate() ?? windowStart;

  const inRange = transactions.filter((transaction) => {
    const transactionCents = toCents(transaction.amount);
    // Merchant orders represent purchases, so they can only apply to money-out register rows.
    if (transactionCents >= 0) {
      return false;
    }
    if (Math.abs(transactionCents + targetCents) > amountToleranceCents) {
      return false;
    }
    if (transaction.postedOn < windowStart || transaction.postedOn > windowEnd) {
      return false;
    }
    // The pre-filter passes anything matching *any* merchant in the batch, so the payee has to be
    // re-checked against this order's merchant. Without it a Walmart order could claim an Amazon
    // transaction at the same amount and date, and a lone survivor reads as unique.
    return matchesPayee(transaction, patterns);
  });

  // The paying card is a preference, not a filter. Prefer transactions on the account it points at,
  // but fall back to every account when it points at nothing — an absent or unresolvable hint should
  // widen the search rather than eliminate the answer.
  const scopedAccountIds = accountIdsForLastFour(accounts, candidate.accountHint);
  const onHintedAccount = inRange.filter(
    (transaction) => transaction.accountId && scopedAccountIds.has(transaction.accountId),
  );

  return (onHintedAccount.length > 0 ? onHintedAccount : inRange)
    .map((transaction) => transaction.id)
    .filter((id): id is string => Boolean(id));
};

/**
 * Matches a batch of order candidates against the register under mutual uniqueness: a pair is
 * accepted only when the order fits exactly one transaction and that transaction is fitted by
 * exactly one order.
 *
 * The batch is deliberate. Matching one order at a time would let iteration order decide the
 * answer — whichever order is processed first takes the transaction, even when a later order fits
 * it just as well. Building the whole adjacency first removes ordering from the result.
 *
 * A transaction is counted as claimed even by an order that is itself ambiguous. That is the
 * conservative reading: an order we cannot resolve is still evidence that the transaction may not
 * belong to the order that *can* claim it. `ambiguousTransaction` in the result makes the cost of
 * that choice visible in real data.
 */
export const matchOrderCandidates = (
  candidates: OrderCandidate[],
  transactions: MatchableTransaction[],
  options: MatchOrderCandidatesOptions = {},
): MatchOrderCandidatesResult => {
  const amountToleranceCents = options.amountToleranceCents ?? DEFAULT_AMOUNT_TOLERANCE_CENTS;
  const dateWindowDays = options.dateWindowDays ?? DEFAULT_DATE_WINDOW_DAYS;
  const override = options.payeePatterns?.map((pattern) => pattern.toLowerCase());
  const patternsByCandidate = candidates.map(
    (candidate) => override ?? payeePatternsForOrder(candidate.order.merchantName),
  );

  // One pass over the register for the whole batch, against the union of every merchant present.
  // Cheap because it is shared; safe because `transactionsForCandidate` narrows to the order's own
  // merchant afterwards.
  const eligible = eligibleTransactions(transactions, [...new Set(patternsByCandidate.flat())], options.today);

  const candidateTransactionIds = candidates.map((candidate, index) =>
    transactionsForCandidate(candidate, eligible, {
      amountToleranceCents,
      dateWindowDays,
      accounts: options.accounts,
      patterns: patternsByCandidate[index],
    }),
  );

  const claimCounts = new Map<string, number>();
  candidateTransactionIds.forEach((ids) => {
    ids.forEach((id) => claimCounts.set(id, (claimCounts.get(id) ?? 0) + 1));
  });

  const matches: MerchantOrderMatch[] = [];
  const skipped: MerchantOrderMatchSkip[] = [];

  candidates.forEach((candidate, index) => {
    const ids = candidateTransactionIds[index];

    if (ids.length === 0) {
      skipped.push({ candidate, reason: merchantOrderSkipReason.noCandidateTransaction });
      return;
    }
    if (ids.length > 1) {
      skipped.push({ candidate, reason: merchantOrderSkipReason.ambiguousOrder });
      return;
    }
    if ((claimCounts.get(ids[0]) ?? 0) > 1) {
      skipped.push({ candidate, reason: merchantOrderSkipReason.ambiguousTransaction });
      return;
    }

    matches.push({ candidate, transactionId: ids[0] });
  });

  return { matches, skipped };
};
